import { CheckCircle2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Box, Button, Link, Paper, Stack, Typography } from '@mui/material'
import { useOnboarding } from '../../context/OnboardingContext'
import { GuideMascotCallout } from '../components/GuideMascotCallout'
import { MISC_PRIMARY, MISC_PRIMARY_HOVER } from '../../theme/onboardingTheme'

const primaryButtonSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  backgroundColor: MISC_PRIMARY,
  px: 2.5,
  py: 1.25,
  fontSize: 14,
  fontWeight: 600,
  color: '#ffffff',
  boxShadow: 1,
  textTransform: 'none' as const,
  '&:hover': { backgroundColor: MISC_PRIMARY_HOVER },
}

type FloridaGeorgiaMississippiGuidePhase = 'login' | 'createUser'

function FloridaGeorgiaMississippiUserAccountDetails() {
  return (
    <Paper
      elevation={0}
      sx={{
        mx: 'auto',
        maxWidth: '42rem',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        backgroundColor: '#ffffff',
        p: 2.5,
        fontSize: 14,
        lineHeight: 1.625,
        color: '#1e293b',
        boxShadow: 1,
      }}
    >
      <Box
        component="dl"
        sx={{
          display: 'grid',
          columnGap: 2,
          rowGap: 1,
          gridTemplateColumns: { sm: 'max-content 1fr' },
          m: 0,
        }}
      >
        <Typography component="dt" sx={{ fontWeight: 600, color: '#0f172a' }}>
          Role:
        </Typography>
        <Typography component="dd" sx={{ m: 0 }}>
          User
        </Typography>
        <Typography component="dt" sx={{ fontWeight: 600, color: '#0f172a' }}>
          Name:
        </Typography>
        <Typography component="dd" sx={{ m: 0 }}>
          Teri Blackshear
        </Typography>
        <Typography component="dt" sx={{ fontWeight: 600, color: '#0f172a' }}>
          Email:
        </Typography>
        <Typography component="dd" sx={{ m: 0 }}>
          <Link
            href="mailto:MISCFilings@morrisisc.com"
            underline="hover"
            sx={{ fontWeight: 500, color: MISC_PRIMARY }}
          >
            MISCFilings@morrisisc.com
          </Link>
        </Typography>
        <Typography component="dt" sx={{ fontWeight: 600, color: '#0f172a' }}>
          Phone:
        </Typography>
        <Typography component="dd" sx={{ m: 0 }}>
          720-502-3980
        </Typography>
        <Typography component="dt" sx={{ fontWeight: 600, color: '#0f172a' }}>
          Username:
        </Typography>
        <Typography component="dd" sx={{ m: 0 }}>
          MISC_[Your Client Name]
        </Typography>
      </Box>
    </Paper>
  )
}

export function StepFloridaGeorgiaMississippiUserAccountAccess() {
  const { dispatch, advance, setStepBackOverride } = useOnboarding()
  const [phase, setPhase] = useState<FloridaGeorgiaMississippiGuidePhase>('login')

  const acknowledgeAndContinue = () => {
    dispatch({ type: 'ACK_FLORIDA_GEORGIA_MISSISSIPPI_USER_ACCOUNT_ACCESS' })
    advance()
  }

  useEffect(() => {
    const handleGuideBack = () => {
      if (phase !== 'createUser') return false
      setPhase('login')
      return true
    }

    setStepBackOverride(handleGuideBack)
    return () => setStepBackOverride(null)
  }, [phase, setStepBackOverride])

  return (
    <Stack spacing={2.5} sx={{ mx: 'auto', maxWidth: '56rem' }}>
      <Box>
        <Typography
          component="h2"
          sx={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em', color: '#0f172a' }}
        >
          Florida/Georgia/Mississippi User Account
        </Typography>
        <Typography component="p" sx={{ mt: 1, fontSize: 14, lineHeight: 1.625, color: '#475569' }}>
          Florida, Georgia, and Mississippi require a user account so MISC can file on your behalf.
        </Typography>
      </Box>

      {phase === 'login' ? (
        <>
          <GuideMascotCallout centered>
            please login to the Administrator Account of the licensed{' '}
            <Box component="strong" sx={{ fontWeight: 700, color: MISC_PRIMARY }}>
              INDIVIDUAL
            </Box>
          </GuideMascotCallout>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button type="button" sx={primaryButtonSx} onClick={() => setPhase('createUser')}>
              I&apos;m logged in!
            </Button>
          </Box>
        </>
      ) : null}

      {phase === 'createUser' ? (
        <>
          <GuideMascotCallout centered>
            Navigate to the Users section and issue an account with the information below!
          </GuideMascotCallout>
          <FloridaGeorgiaMississippiUserAccountDetails />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              borderRadius: '12px',
              border: '1px solid #bfdbfe',
              backgroundColor: '#eff6ff',
              px: 2,
              py: 2,
              textAlign: 'center',
            }}
          >
            <Button
              type="button"
              sx={primaryButtonSx}
              onClick={acknowledgeAndContinue}
              startIcon={<CheckCircle2 size={16} />}
            >
              I&apos;ve created an account for MISC!
            </Button>
          </Box>
        </>
      ) : null}
    </Stack>
  )
}
